/**
 * Team-level partnership policy shared by auth bootstrap, route guards, and
 * navigation. Product mode is the coarse switch; uiPolicy carries the concrete
 * surfaces to hide.
 */
import type { ExamLevel } from './examLevels.js';
import type { OrgLevel } from './orgLevels.js';

export const PARTNERSHIP_KINDS = Object.freeze(['nonprofit'] as const);
export type PartnershipKind = (typeof PARTNERSHIP_KINDS)[number];

export const PARTNERSHIP_STATUSES = Object.freeze([
  'active',
  'paused',
  'ended',
] as const);
export type PartnershipStatus = (typeof PARTNERSHIP_STATUSES)[number];

export const PRODUCT_MODES = Object.freeze([
  'standard',
  'adaptive_only',
] as const);
export type ProductMode = (typeof PRODUCT_MODES)[number];

export interface TeamUiPolicy {
  hideClassCodeUi: boolean;
}

export interface TeamPolicy {
  partnershipKind: PartnershipKind | null;
  cohortLabel: string | null;
  orgLevel: OrgLevel | null;
  examLevel: ExamLevel | null;
  partnershipStatus: PartnershipStatus | null;
  productMode: ProductMode;
  uiPolicy: TeamUiPolicy;
}

export const DEFAULT_TEAM_UI_POLICY: TeamUiPolicy = Object.freeze({
  hideClassCodeUi: false,
});

export const DEFAULT_TEAM_POLICY: TeamPolicy = Object.freeze({
  partnershipKind: null,
  cohortLabel: null,
  orgLevel: null,
  examLevel: null,
  partnershipStatus: null,
  productMode: 'standard',
  uiPolicy: DEFAULT_TEAM_UI_POLICY,
});

const isOneOf = <T extends readonly string[]>(
  value: unknown,
  values: T
): value is T[number] =>
  typeof value === 'string' && (values as readonly string[]).includes(value);

const stringOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export function normalizeTeamPolicy(
  value: Partial<TeamPolicy> | null | undefined
): TeamPolicy {
  const productMode = isOneOf(value?.productMode, PRODUCT_MODES)
    ? value.productMode
    : DEFAULT_TEAM_POLICY.productMode;

  const rawUiPolicy: Partial<TeamUiPolicy> = value?.uiPolicy ?? {};
  const adaptiveOnly = productMode === 'adaptive_only';
  const uiPolicy = {
    hideClassCodeUi: adaptiveOnly || rawUiPolicy.hideClassCodeUi === true,
  };

  return {
    partnershipKind: isOneOf(value?.partnershipKind, PARTNERSHIP_KINDS)
      ? value.partnershipKind
      : null,
    cohortLabel: stringOrNull(value?.cohortLabel),
    orgLevel: (stringOrNull(value?.orgLevel) as OrgLevel | null) ?? null,
    examLevel: (stringOrNull(value?.examLevel) as ExamLevel | null) ?? null,
    partnershipStatus: isOneOf(value?.partnershipStatus, PARTNERSHIP_STATUSES)
      ? value.partnershipStatus
      : null,
    productMode,
    uiPolicy,
  };
}

export function isAdaptiveOnlyTeamPolicy(
  policy: TeamPolicy | null | undefined
): boolean {
  return policy?.productMode === 'adaptive_only';
}
